import React from 'react';
import type { AiThemeHat, AiThemeWeather } from './ai-theme.model';

/**
 * Theme costumes shared by every AI character: a hat drawn around its brim
 * (0, 0), up being negative y, and weather that fills the character's box.
 */

const WHITE = '#FFFFFF';
const EDGE = 'rgba(0,0,0,0.15)';
const GREEN = '#5E9A4E';
const FLAME = '#F6B33E';

/** A ring of small flowers or leaves along a gently curved band. */
const crownPoints = [-13, -6.5, 0, 6.5, 13].map((x): [number, number] => [
  x,
  -1.5 + 0.012 * x * x,
]);

const HATS: Record<AiThemeHat, React.ReactNode> = {
  none: null,
  beanie: (
    <>
      <path d="M -14 1 Q -14 -15 0 -15 Q 14 -15 14 1 Z" fill="#C9544B" />
      <rect x={-15} y={-3} width={30} height={5.5} rx={2.5} fill="#A9433C" />
      <circle cx={0} cy={-16.5} r={4} fill="#F2EFE9" />
    </>
  ),
  straw: (
    <>
      <ellipse cx={0} cy={0} rx={19} ry={3.5} fill="#E5C27A" />
      <path d="M -9 0 Q -9 -11 0 -11 Q 9 -11 9 0 Z" fill="#D9B263" />
      <rect x={-9} y={-3.5} width={18} height={2.5} fill="#E05C5C" />
    </>
  ),
  santa: (
    <>
      <path
        d="M -13 1 Q -12 -14 3 -18 Q 12 -20 16 -10 L 12 -9 Q 9 -13 4 -10 Q 9 -4 13 1 Z"
        fill="#D9473F"
      />
      <rect
        x={-15}
        y={-2}
        width={30}
        height={5}
        rx={2.5}
        fill={WHITE}
        stroke={EDGE}
        strokeWidth={0.6}
      />
      <circle
        cx={15.5}
        cy={-9}
        r={3.5}
        fill={WHITE}
        stroke={EDGE}
        strokeWidth={0.6}
      />
    </>
  ),
  lucia: (
    <>
      {[-10, -3.5, 3.5, 10].map((x) => (
        <g key={x}>
          <rect
            x={x - 1.5}
            y={-11}
            width={3}
            height={10}
            rx={0.8}
            fill={WHITE}
            stroke={EDGE}
            strokeWidth={0.6}
          />
          <ellipse cx={x} cy={-13.5} rx={1.6} ry={2.6} fill={FLAME}>
            <animate
              attributeName="ry"
              values="2.6;2;2.8;2.6"
              dur={`${0.9 + Math.abs(x) * 0.03}s`}
              repeatCount="indefinite"
            />
          </ellipse>
        </g>
      ))}
      <path
        d="M -15 0 Q 0 4 15 0"
        fill="none"
        stroke={GREEN}
        strokeWidth={4}
        strokeLinecap="round"
      />
    </>
  ),
  witch: (
    <>
      <ellipse cx={0} cy={0} rx={18} ry={3.2} fill="#3A3046" />
      <path
        d="M -9 0 Q -3 -12 3 -27 Q 5 -19 11 -15 Q 8 -12 9 0 Z"
        fill="#3A3046"
      />
      <rect x={-8.5} y={-4.5} width={17.5} height={3} fill="#EDA239" />
    </>
  ),
  bunny: (
    <>
      {[-1, 1].map((side) => (
        <g key={side} transform={`rotate(${side * 14} ${side * 6} 0)`}>
          <ellipse
            cx={side * 6}
            cy={-12}
            rx={4.5}
            ry={12}
            fill={WHITE}
            stroke={EDGE}
            strokeWidth={0.6}
          />
          <ellipse cx={side * 6} cy={-12} rx={2.2} ry={8.5} fill="#F2B8C6" />
        </g>
      ))}
    </>
  ),
  flowers: (
    <>
      <path
        d="M -15 -1 Q 0 3 15 -1"
        fill="none"
        stroke={GREEN}
        strokeWidth={2.5}
        strokeLinecap="round"
      />
      {crownPoints.map(([x, y], i) => (
        <g key={x}>
          <circle
            cx={x}
            cy={y - 1}
            r={3.4}
            fill={['#FFFFFF', '#F28FB0', '#9DB8F2', '#F28FB0', '#FFFFFF'][i]}
            stroke={EDGE}
            strokeWidth={0.5}
          />
          <circle cx={x} cy={y - 1} r={1.2} fill="#F4D35E" />
        </g>
      ))}
    </>
  ),
  party: (
    <>
      <path d="M -9 1 L 0 -22 L 9 1 Z" fill="#E05C8A" />
      <path
        d="M -5.5 -8 L 5.5 -8 M -7.5 -3 L 7.5 -3 M -3.5 -14 L 3.5 -14"
        stroke="#F4D35E"
        strokeWidth={2}
      />
      <circle cx={0} cy={-22.5} r={3} fill="#9DB8F2" />
    </>
  ),
};

/** The theme's hat; the character moves the group onto its head. */
export const ThemeHat = React.forwardRef<SVGGElement, { hat: AiThemeHat }>(
  ({ hat }, ref) => (
    <g ref={ref} opacity={0}>
      {HATS[hat]}
    </g>
  ),
);
ThemeHat.displayName = 'AiThemeHat';

/** The same scatter every render: x, delay and size factor of each particle. */
const scatter = (count: number, seed: number) =>
  Array.from({ length: count }, (_, i) => {
    const r = (n: number) => {
      const v = Math.sin((i + 1) * 12.9898 + seed * 78.233 + n) * 43758.5453;
      return v - Math.floor(v);
    };
    return { x: 4 + r(1) * 103, delay: r(2), size: 0.7 + r(3) * 0.6 };
  });

/**
 * Particles that drift down through the box, swaying and turning, each on
 * its own delay so they never fall in step.
 */
const Falling: React.FC<{
  count: number;
  seed: number;
  /** Seconds to fall the whole box. */
  duration: number;
  sway: number;
  spin: boolean;
  shape: (i: number, size: number) => React.ReactNode;
}> = ({ count, seed, duration, sway, spin, shape }) => (
  <>
    {scatter(count, seed).map(({ x, delay, size }, i) => {
      const dur = duration * (0.8 + 0.4 * size);
      const begin = `${(-delay * dur).toFixed(2)}s`;
      return (
        <g key={i}>
          <animateTransform
            attributeName="transform"
            type="translate"
            values={`${x} -8; ${x + sway} 40; ${x - sway} 80; ${x} 120`}
            dur={`${dur.toFixed(2)}s`}
            begin={begin}
            repeatCount="indefinite"
          />
          <g>
            {spin && (
              <animateTransform
                attributeName="transform"
                type="rotate"
                values="0; 360"
                dur={`${(dur / 2).toFixed(2)}s`}
                begin={begin}
                repeatCount="indefinite"
              />
            )}
            {shape(i, size)}
          </g>
        </g>
      );
    })}
  </>
);

const LEAF_COLOURS = ['#D9733B', '#C9544B', '#E2B04A'];
const CONFETTI_COLOURS = ['#E05C8A', '#F4D35E', '#9DB8F2', '#5E9A4E'];

/** A small bat that flaps across the top of the box. */
const Bat: React.FC<{ y: number; duration: number; delay: number }> = ({
  y,
  duration,
  delay,
}) => (
  <g>
    <animateTransform
      attributeName="transform"
      type="translate"
      values={`-10 ${y}; 40 ${y - 6}; 80 ${y + 3}; 121 ${y - 4}`}
      dur={`${duration}s`}
      begin={`${-delay}s`}
      repeatCount="indefinite"
    />
    <g>
      <animateTransform
        attributeName="transform"
        type="scale"
        values="1.6 1.6; 1.6 0.55; 1.6 1.6"
        dur="0.35s"
        repeatCount="indefinite"
      />
      <path
        d="M 0 0 Q -3 -3 -8 -2 Q -6 0 -7 2 Q -4 1 -2 2 Q -1 1 0 2 Q 1 1 2 2 Q 4 1 7 2 Q 6 0 8 -2 Q 3 -3 0 0 Z"
        fill="#3A3046"
      />
    </g>
  </g>
);

const WEATHER: Record<AiThemeWeather, React.ReactNode> = {
  none: null,
  snow: (
    <Falling
      count={12}
      seed={1}
      duration={6}
      sway={4}
      spin={false}
      shape={(_, size) => <circle r={1.6 * size} fill="#C9D6E3" />}
    />
  ),
  blossoms: (
    <Falling
      count={7}
      seed={2}
      duration={8}
      sway={8}
      spin
      shape={(_, size) => (
        <ellipse rx={2.6 * size} ry={1.5 * size} fill="#F2B8C6" />
      )}
    />
  ),
  feathers: (
    <Falling
      count={6}
      seed={3}
      duration={9}
      sway={10}
      spin
      shape={(i, size) => (
        <ellipse
          rx={1.4 * size}
          ry={4 * size}
          fill={i % 2 === 0 ? '#F4D35E' : '#F28FB0'}
        />
      )}
    />
  ),
  leaves: (
    <Falling
      count={6}
      seed={4}
      duration={7}
      sway={9}
      spin
      shape={(i, size) => (
        <path
          d="M 0 -4 Q 3.5 -1 0 4 Q -3.5 -1 0 -4 Z"
          transform={`scale(${size.toFixed(2)})`}
          fill={LEAF_COLOURS[i % LEAF_COLOURS.length]}
        />
      )}
    />
  ),
  confetti: (
    <Falling
      count={12}
      seed={5}
      duration={5}
      sway={5}
      spin
      shape={(i, size) => (
        <rect
          x={-1.5 * size}
          y={-2.5 * size}
          width={3 * size}
          height={5 * size}
          fill={CONFETTI_COLOURS[i % CONFETTI_COLOURS.length]}
        />
      )}
    />
  ),
  bats: (
    <>
      <Bat y={14} duration={7} delay={0} />
      <Bat y={24} duration={9} delay={4} />
    </>
  ),
  sun: (
    <g transform="translate(96 13)">
      <g>
        <animateTransform
          attributeName="transform"
          type="rotate"
          values="0; 360"
          dur="24s"
          repeatCount="indefinite"
        />
        {Array.from({ length: 8 }, (_, i) => (
          <rect
            key={i}
            x={-1}
            y={-12}
            width={2}
            height={4}
            rx={1}
            fill="#F4C542"
            transform={`rotate(${i * 45})`}
          />
        ))}
      </g>
      <circle r={6} fill="#F4C542" />
    </g>
  ),
};

/** The theme's weather, drawn behind the character. */
export const ThemeWeather: React.FC<{ weather: AiThemeWeather }> = ({
  weather,
}) => <g pointerEvents="none">{WEATHER[weather]}</g>;
